import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import { Message } from "../interfaces";

const generateRandomToken = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
};

export type WebChat = {
  webId: string | null | undefined;
  companyId: string | null | undefined;
  name?: string;
  localOnly: boolean;
};

export type WebChats = {
  loading: boolean;
  webChats?: WebChat[];
};

interface WebChatState extends WebChats {
  setWebChats: (webChats: WebChats) => void;
  setLoading: (loading: boolean) => void;
  createWebId: (companyId: string) => void;
  activateWebId: (companyId: string, webId: string) => void;
  deActivateWebId: (companyId: string, webId: string) => void;
  reset: () => void;
}

const webChatInitialState: WebChats = {
  loading: false,
  webChats: undefined,
};

const useWebChatStore = create(
  persist(
    devtools<WebChatState>(
      (set, get) => ({
        ...webChatInitialState,
        setWebChats: (webChats) => {
          return set(webChats);
        },
        setLoading: (loading) => {
          return set({ loading });
        },
        deActivateWebId: async (companyId, webId) => {
          const state = get() || {};
          const { webChats } = state;
          if (!webChats) return;

          // find webId and set localOnly to false
          const companyWebChat = state?.webChats?.find(
            (play) => play.companyId === companyId && webId === play.webId
          );
          if (companyWebChat) {
            const newWebChats = webChats.map((play) => {
              if (play.webId === webId) {
                return { ...play, localOnly: true };
              }
              return play;
            });
            set({ webChats: newWebChats });
          } else {
            // if webId not found, create a new one
            const newWebChats = [
              ...(webChats || []),
              { webId, companyId, localOnly: true },
            ];
            set({ webChats: newWebChats });
          }
        },
        activateWebId: async (companyId, webId) => {
          const state = get() || {};
          const companyWebChat = state?.webChats?.find(
            (play) => play.companyId === companyId && webId === play.webId
          );
          if (companyWebChat && state.webChats) {
            const newWebChats = state.webChats?.map((play) => {
              if (play.companyId === companyId) {
                return { ...play, webId, localOnly: false };
              }
              return play;
            });
            set({ webChats: newWebChats });
          } else {
            const newWebChats = [
              ...(state.webChats || []),
              { webId, companyId, localOnly: false },
            ];
            set({ webChats: newWebChats });
          }
        },
        createWebId: async (companyId, name = "Webchat user") => {
          const state = get() || {};
          const companyWebChat = state?.webChats?.find(
            (play) => play.companyId === companyId
          );

          if (companyWebChat && companyWebChat.webId) {
            return set({ loading: false });
          }
          const webId = generateRandomToken();
          const newWebChats = [
            ...(state.webChats || []),
            { webId: webId, companyId, name, localOnly: true },
          ];
          return set({ webChats: newWebChats, loading: false });
        },
        reset: () => set({ ...webChatInitialState }),
      }),
      { name: "useWebChatStore" }
    ),
    { name: "webChats" }
  )
);

export const useWebChatData = (companyId: string | null) =>
  useWebChatStore((state) => ({
    webChats: state.webChats,
    webChat: state?.webChats?.find((play) => play.companyId === companyId),
    loading: state.loading,
  }));

export const useWebChatActions = () =>
  useWebChatStore((state) => ({
    setWebChats: state.setWebChats,
    setLoading: state.setLoading,
    createWebId: state.createWebId,
    deActivateWebId: state.deActivateWebId,
    activateWebId: state.activateWebId,
    reset: state.reset,
  }));

export default useWebChatStore;
